/**
 * ADM-29 校区管理页（org-campus:SC-05~SC-32 前端）。
 *
 * 筛选区：名称/编码输入 + 状态下拉 + 查询/重置。
 * 列表表格：编码、名称、地址、时区、员工数、学生数、班级数、状态、操作（编辑/停用/启用）。
 * 新建/编辑：对话框表单（编码新建可填/编辑禁用、名称、地址、时区；编辑携带 version）。
 * 停用/启用：二次确认弹窗。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { EditDialog } from "../../components/EditDialog";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import type { ColumnsType } from "antd/es/table";
import {
  orgCampusApi,
  type CampusItem,
  type CampusStatus,
  type CreateCampus,
  type UpdateCampus,
} from "../../api/campuses.api";
import { ApiError } from "../../api/transport";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";

const { Title } = Typography;

/** 校区状态标签映射。 */
function CampusStatusTag({ status }: { status: CampusStatus }) {
  const map: Record<CampusStatus, { color: string; label: string }> = {
    ACTIVE: { color: "green", label: "正常" },
    SUSPENDED: { color: "default", label: "已停用" },
  };
  const cfg = map[status] ?? { color: "default", label: status };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
}

/** 提取 ApiError 的错误码信息。 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function CampusesPage() {
  const [data, setData] = useState<CampusItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<{
    name?: string;
    code?: string;
    status?: CampusStatus;
  }>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CampusItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateCampus & { version?: number }>();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await orgCampusApi.listCampuses({
        ...filters,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setDialogOpen(true);
  };

  const openEdit = (record: CampusItem) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      numericCode: record.numericCode,
      name: record.name,
      address: record.address ?? undefined,
      timezone: record.timezone,
      version: record.version,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editing) {
        const update: UpdateCampus = {
          name: values.name,
          address: values.address,
          timezone: values.timezone,
          version: values.version ?? editing.version,
        };
        await orgCampusApi.updateCampus(editing.id, update);
        message.success("校区信息已更新");
      } else {
        const create: CreateCampus = {
          code: values.code,
          numericCode: values.numericCode,
          name: values.name,
          address: values.address,
          timezone: values.timezone,
        };
        await orgCampusApi.createCampus(create);
        message.success("校区已创建");
      }
      setDialogOpen(false);
      void fetchList();
    } catch (err) {
      if (err instanceof ApiError) {
        message.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspend = async (id: string) => {
    try {
      await orgCampusApi.suspendCampus(id);
      message.success("校区已停用");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await orgCampusApi.activateCampus(id);
      message.success("校区已启用");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  // 行操作统一 EDIT 闸控（对齐后端 @Roles）；无权限整列不渲染，越权由后端 403 兜底
  const canManage = useHasPermission("ADM-29", "EDIT");
  const { columns, components } = useResizableColumns<CampusItem>([
    { title: "校区编码", dataIndex: "code", width: 100, ellipsis: true },
    { title: "数字码", dataIndex: "numericCode", width: 90 },
    { title: "校区名称", dataIndex: "name", width: 160, ellipsis: true },
    { title: "地址", dataIndex: "address", width: 220, ellipsis: true },
    { title: "时区", dataIndex: "timezone", width: 140 },
    { title: "员工数", dataIndex: "staffCount", width: 80, align: "center" },
    {
      title: "在读学生数",
      dataIndex: "studentCount",
      width: 90,
      align: "center",
    },
    {
      title: "进行中班级数",
      dataIndex: "activeClassCount",
      width: 100,
      align: "center",
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (status: CampusStatus) => <CampusStatusTag status={status} />,
    },
    ...(canManage
      ? ([
          {
            title: "操作",
            fixed: "right",
            key: "action",
            width: 160,
            render: (_, record) => (
              <Space size="small">
                <Button
                  type="link"
                  size="small"
                  onClick={() => openEdit(record)}
                >
                  编辑
                </Button>
                {record.status === "ACTIVE" ? (
                  <Popconfirm
                    title="确定停用该校区吗？"
                    description="停用后不可新建班级、报名、排课"
                    onConfirm={() => handleSuspend(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>
                      停用
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="确定启用该校区吗？"
                    onConfirm={() => handleActivate(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small">
                      启用
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ] as ColumnsType<CampusItem>)
      : []),
  ]);

  return (
    <div>
      <Title level={4}>校区管理</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="校区名称"
            allowClear
            value={filters.name ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, name: e.target.value || undefined }))
            }
            style={{ width: 180 }}
          />
          <Input
            placeholder="校区编码"
            allowClear
            value={filters.code ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, code: e.target.value || undefined }))
            }
            style={{ width: 140 }}
          />
          <Select
            placeholder="状态"
            allowClear
            value={filters.status}
            onChange={(val) =>
              setFilters((f) => ({ ...f, status: val ?? undefined }))
            }
            style={{ width: 120 }}
            options={[
              { value: "ACTIVE", label: "正常" },
              { value: "SUSPENDED", label: "已停用" },
            ]}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => void fetchList()}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 无 ADM-29 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
          <HasPermission menuKey="ADM-29" action="CREATE">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新建校区
            </Button>
          </HasPermission>
        </div>
        <Table<CampusItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>
      <EditDialog
        title={editing ? "编辑校区" : "新建校区"}
        open={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={submitting}
        okText="保存"
        cancelText="取消"
        width={420}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="校区编码"
            rules={[
              { required: true, message: "请输入校区编码" },
              {
                pattern: /^[A-Za-z]{1,10}$/,
                message: "编码只能由 1-10 个字母组成",
              },
            ]}
          >
            <Input placeholder="如 GM" disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="numericCode"
            label="校区数字码"
            rules={[
              { required: true, message: "请输入校区数字码" },
              {
                pattern: /^[0-9]{4}$/,
                message: "数字码必须为 4 位纯数字",
              },
            ]}
            extra="4 位纯数字（如 0001），作为学号前缀，创建后不可修改"
          >
            <Input placeholder="如 0001" maxLength={4} disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="name"
            label="校区名称"
            rules={[{ required: true, message: "请输入校区名称" }]}
          >
            <Input placeholder="如 高新校区" />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input placeholder="校区地址" />
          </Form.Item>
          <Form.Item name="timezone" label="时区">
            <Input placeholder="如 Asia/Shanghai（留空继承机构默认）" />
          </Form.Item>
        </Form>
      </EditDialog>
    </div>
  );
}
